"use client";

import { useEffect, useMemo, useState } from "react";
import GlowOrb from "@/components/glow-orb";

type GlowResearchProgressProps = {
  businessName: string;
  businessType: string;
  website: string;
  addressOrArea: string;
  googleBusinessProfile: string;
  selectedPlace: any;
  businessContext: string;
  status?: string;
};

type ResearchStep = {
  title: string;
  detail: string;
};

function shorten(value: unknown, maximumLength = 76): string {
  const text = String(value || "").trim();

  if (text.length <= maximumLength) {
    return text;
  }

  return `${text.slice(0, maximumLength - 1).trim()}…`;
}

function displayWebsite(value: string): string {
  return value
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/$/, "");
}

export default function GlowResearchProgress({
  businessName,
  businessType,
  website,
  addressOrArea,
  googleBusinessProfile,
  selectedPlace,
  businessContext,
  status,
}: GlowResearchProgressProps) {
  const steps = useMemo<ResearchStep[]>(() => {
    const nextSteps: ResearchStep[] = [
      {
        title: "Business brief received",
        detail: [
          businessName.trim() || "Business name supplied",
          businessType.trim(),
        ]
          .filter(Boolean)
          .join(" · "),
      },
    ];

    if (website.trim()) {
      nextSteps.push({
        title: "Website added to the research brief",
        detail: displayWebsite(website),
      });
    } else {
      nextSteps.push({
        title: "Public customer paths are being considered",
        detail:
          "Glow is checking whether the business appears to sell locally, online or through a mixture.",
      });
    }

    if (selectedPlace) {
      nextSteps.push({
        title: "Google listing selected",
        detail: shorten(
          selectedPlace.name ||
            selectedPlace.address ||
            businessName ||
            "Selected Google listing",
        ),
      });
    } else if (googleBusinessProfile.trim()) {
      nextSteps.push({
        title: "Google Maps link added",
        detail:
          "Glow will use the owner-supplied link as part of the business context.",
      });
    } else if (addressOrArea.trim()) {
      nextSteps.push({
        title: "Location context added",
        detail: shorten(addressOrArea),
      });
    } else {
      nextSteps.push({
        title: "Location model being assessed",
        detail:
          "Glow is considering whether customers visit, book remotely or receive services elsewhere.",
      });
    }

    if (businessContext.trim()) {
      nextSteps.push({
        title: "Owner priorities added",
        detail: shorten(businessContext, 92),
      });
    }

    nextSteps.push(
      {
        title: "Mapping how the business sells",
        detail:
          "Looking for physical, online, appointment, enquiry and quotation routes.",
      },
      {
        title: "Tracing customer journeys",
        detail:
          "Reviewing how a potential customer discovers, evaluates and contacts the business.",
      },
      {
        title: "Identifying offers and customer groups",
        detail:
          "Preparing evidence-based suggestions for the owner to confirm or correct.",
      },
      {
        title: "Preparing the first useful opportunity",
        detail:
          "Glow is looking for one specific early win rather than generic advice.",
      },
    );

    return nextSteps;
  }, [
    addressOrArea,
    businessContext,
    businessName,
    businessType,
    googleBusinessProfile,
    selectedPlace,
    website,
  ]);

  const [visibleCount, setVisibleCount] = useState(1);

  useEffect(() => {
    setVisibleCount(1);

    const timer = window.setInterval(() => {
      setVisibleCount((current) => {
        if (current >= steps.length) {
          window.clearInterval(timer);
          return current;
        }

        return current + 1;
      });
    }, 780);

    return () => {
      window.clearInterval(timer);
    };
  }, [steps.length]);

  const visibleSteps = steps.slice(0, visibleCount);

  return (
    <div aria-live="polite">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
        <div className="flex justify-center sm:justify-start">
          <GlowOrb
            state="working"
            size="lg"
            label={`Glow is building the Business Map for ${
              businessName || "your business"
            }`}
          />
        </div>

        <div className="flex-1">
          <p className="text-sm font-light uppercase tracking-[0.2em] text-[#007c7a]">
            Glow research in progress
          </p>

          <h3 className="mt-3 text-3xl font-light leading-tight text-[#092723]">
            Glow is building the Business Map
            {businessName.trim() ? ` for ${businessName.trim()}` : ""}.
          </h3>

          <p className="mt-4 text-base font-light leading-7 text-[#52635f]">
            {status ||
              "Researching how the business sells, serves customers and creates value."}
          </p>

          <p className="mt-3 text-xs font-light leading-5 text-[#71817d]">
            Glow reports genuine research steps rather than showing a fake
            percentage or countdown.
          </p>
        </div>
      </div>

      <div className="mt-7 space-y-3">
        {visibleSteps.map((step, index) => {
          const isCurrent = index === visibleSteps.length - 1;
          const isComplete = !isCurrent || visibleCount === steps.length;

          return (
            <div
              key={`${step.title}-${index}`}
              className="motion-safe:animate-[pulse_0.55s_ease-out_1] flex items-start gap-4 rounded-3xl border border-[#d7e5df] bg-[#f4fbf8] p-4 transition"
            >
              <div
                className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs ${
                  isComplete
                    ? "border-[#9dd8ce] bg-[#e3f7f2] text-[#007c7a]"
                    : "motion-safe:animate-pulse border-[#61e6dc] bg-[#092723] text-[#61e6dc]"
                }`}
                aria-hidden="true"
              >
                {isComplete ? "✓" : "•"}
              </div>

              <div className="min-w-0">
                <p className="text-sm font-normal text-[#092723]">
                  {step.title}
                </p>

                <p className="mt-1 text-xs font-light leading-5 text-[#52635f]">
                  {step.detail}
                </p>
              </div>

              {isCurrent && !isComplete && (
                <span className="ml-auto shrink-0 rounded-full border border-[#b9e3de] bg-white px-3 py-1 text-[10px] font-light uppercase tracking-[0.12em] text-[#007c7a]">
                  Working
                </span>
              )}
            </div>
          );
        })}

        {visibleCount < steps.length && (
          <div className="flex items-center gap-3 px-4 py-2 text-xs font-light text-[#71817d]">
            <span className="h-2 w-2 motion-safe:animate-pulse rounded-full bg-[#61e6dc]" />
            More research activity will appear here.
          </div>
        )}
      </div>

      <div className="mt-7 rounded-3xl border border-[#b9e3de] bg-[#092723] p-5 text-white">
        <p className="text-xs font-light uppercase tracking-[0.18em] text-[#61e6dc]">
          What happens next
        </p>

        <p className="mt-2 text-sm font-light leading-6 text-[#d7e5df]">
          Glow will turn this research into a draft Business Map, one credible
          early opportunity and a small number of findings for you to confirm.
        </p>
      </div>
    </div>
  );
}
